import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Tabs, TabsList, TabsTrigger } from './components/ui/tabs';
import { Alert, AlertDescription } from './components/ui/alert';
import { Activity, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import Dashboard from './Dashboard';

// API Configuration - UPDATE THIS FOR PRODUCTION
// const API_URL = 'http://localhost:8080';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
// For production, change to: const API_URL = 'https://yourdomain.com';

export default function App() {
  const token = localStorage.getItem('authToken');
  
  if (token) {
    return <Dashboard />;
  }
  
  return <LoginPage />;
}

function LoginPage() {
  const [userType, setUserType] = useState('user');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Form data
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      // Login endpoint
      const endpoint = userType === 'publisher' 
        ? `${API_URL}/api/publishers/login`
        : `${API_URL}/api/auth/login`;
      
      // Login uses form-urlencoded
      const body = new URLSearchParams({
        username: userType === 'publisher' ? email : username,
        password: password
      });
      
      const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };

      console.log('Making request to:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: body
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      // Success! Store authentication data
      console.log('Authentication successful:', data);
      
      localStorage.setItem('authToken', data.access_token);
      localStorage.setItem('userType', userType);
      localStorage.setItem('currentUser', JSON.stringify(data.user || data.publisher || {}));

      setSuccess(true);
      
      // Show success message then reload
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (err) {
      console.error('Authentication error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-1 pb-6">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full shadow-lg">
                <Activity className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center text-gray-900">
              Campaign Tracker
            </CardTitle>
            <CardDescription className="text-center text-gray-600">
              Sign in to manage your campaigns
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* User Type Tabs */}
            <div className="w-full">
              <Tabs value={userType} onValueChange={setUserType}>
                <TabsList className="grid w-full grid-cols-2 bg-gray-100">
                  <TabsTrigger value="user" className="data-[state=active]:bg-white">
                    Company
                  </TabsTrigger>
                  <TabsTrigger value="publisher" className="data-[state=active]:bg-white">
                    Publisher
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className="space-y-4">
              {/* Email field for publishers */}
              {userType === 'publisher' && (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="publisher@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="h-11"
                  />
                </div>
              )}

              {/* Username field for companies */}
              {userType === 'user' && (
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-gray-700 font-medium">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="johndoe"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loading}
                    className="h-11"
                  />
                </div>
              )}

              {/* Password field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !loading) {
                      handleSubmit();
                    }
                  }}
                  className="h-11"
                />
              </div>

              {/* Error Alert */}
              {error && (
                <Alert variant="destructive" className="bg-red-50 border-red-200">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 ml-2">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800 ml-2">
                    Login successful! Redirecting...
                  </AlertDescription>
                </Alert>
              )}

              {/* Submit Button */}
              <Button 
                onClick={handleSubmit}
                className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium" 
                disabled={loading || success}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign In
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}